// @ts-nocheck
import { createStandard } from '../_data_sources/AccreditationStandards/manageStandard';
import { fetchAccreditationStandards } from '../_data_sources/AccreditationStandards/useStandards';
import type { AccreditationStandardNew, AccreditationStandardList } from '../_types/awb';
import caleaData from './calea.json';
import { randParagraph, randNumber } from '@ngneat/falso';
import { baseApiUrl, fetcher } from 'data_sources/constants';
import { createParamString } from 'data_sources/constants';
import { createWrittenDirective } from '../_data_sources/WrittenDirectives/manageWrittenDirectives';
import { get } from 'lodash';

declare global {
  interface Window {
    setCaleaStandards: (createWrittenDirectives?: boolean) => Promise<void>;
  }
}

const randomContent = () => {
  const numParagraphs = randNumber({ min: 1, max: 5 });
  const paragraphs = Array.from({ length: numParagraphs }, () => {
    const numLines = randNumber({ min: 1, max: 10 });
    return `<p>${randParagraph({ length: numLines })}</p>`;
  });
  return paragraphs.join('');
};

// Cache for Law Enforcement manuals
let leManualsCache: any[] | null = null;

const fetchLEManuals = async (manualIds: string[]) => {
  if (leManualsCache) return leManualsCache;

  const manuals = await Promise.all(
    manualIds.map(manualId => 
      fetcher({
        method: 'get',
        url: `${baseApiUrl()}/lexipub/manuals/issued/v1/manuals/${manualId}`,
      })
    )
  );

  leManualsCache = manuals;
  return leManualsCache;
};

const searchManualContent = async (searchText: string) => {
  const manuals = await fetchLEManuals([]);
  const matches = [];
  // Escape special regex characters and create case-insensitive regex
  const searchRegex = new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
  console.log('searchRegex', searchRegex);

  // Search through cached manuals
  manuals.forEach(manual => {
    manual.chapters?.forEach(chapter => {
      chapter.policies?.forEach(policy => {
        // Check policy title and content
        const policyTitle = get(policy, 'title.value', get(policy, 'title', ''));
        // console.log('policy', policyTitle);
        if (searchRegex.test(policyTitle)) {
          matches.push({
            contentUri: `xref://manuals/${manual.guid}/content/${policy.guid}`,
            title: policyTitle,
            content: '',
            manualId: manual.guid
          });
        }

        // Check sections
        policy.sections?.forEach(section => {
          const sectionTitle = get(section, 'title.value', get(section, 'title', ''));
          const sectionContent = get(section, 'content.value', get(section, 'content', ''));
          // console.log('section', sectionTitle, sectionContent);
          if (searchRegex.test(sectionTitle) || searchRegex.test(sectionContent)) {
            matches.push({
              contentUri: `xref://manuals/${manual.guid}/content/${section.guid}`,
              title: sectionTitle,
              content: sectionContent,
              manualId: manual.guid
            });
          }

          // Check subsections
          section.subSections?.forEach(subsection => {
            const subsectionTitle = get(subsection, 'title.value', get(subsection, 'title', ''));
            const subsectionContent = get(subsection, 'content.value', get(subsection, 'content', ''));
            // console.log('subsection', subsectionTitle, subsectionContent);
            if (searchRegex.test(subsectionTitle) || searchRegex.test(subsectionContent)) {
              matches.push({
                contentUri: `xref://manuals/${manual.guid}/content/${subsection.guid}`,
                title: subsectionTitle,
                content: subsectionContent,
                manualId: manual.guid
              });
            }
          });
        });
      });
    });
    console.log('matches', matches);
  });

  return matches;
};

window.setCaleaStandards = async (createWrittenDirectives = false) => {
  console.log('Starting setCaleaStandards...');
  console.log('baseApiUrl()', baseApiUrl());
  console.log('createWrittenDirectives:', createWrittenDirectives);
  
  try {
    // Extract IDs from URL path
    const pathMatch = window.location.pathname.match(
      /\/accrediting-bodies\/([^/]+)\/versions\/([^/]+)/
    );
    console.log('pathMatch', pathMatch, window.location.pathname);

    if (!pathMatch) {
      throw new Error('Could not find accreditationId and versionId in URL path');
    }

    const [, accreditationId, versionId] = pathMatch;

    // Fetch existing standards to avoid duplicates
    console.log('Fetching existing standards...');
    const existingStandards = await fetchAccreditationStandards(accreditationId, versionId);
    console.log('Existing standards count:', existingStandards.length);

    // Create a map of existing standards by heading for quick lookup
    const existingStandardsMap = new Map<string, AccreditationStandardList>();
    existingStandards.forEach(standard => {
      existingStandardsMap.set(standard.heading, standard);
    });

    // Fetch all manuals
    const url = `${baseApiUrl()}/lexipub/manuals/issued/v1/manuals`;
    const params = {
      organizationId: accreditationId
    };
    const paramString = createParamString(params);
    
    const response = await fetcher({
      method: 'get',
      url: `${url}?${paramString}`,
    });

    // Create manual ID to jurisdiction ID mapping
    const manualJurisdictionMap = new Map();
    const leManualIds: string[] = [];
    response.content
      .filter(manual => manual.jurisdiction && manual.vertical?.value === 'Law Enforcement')
      .forEach(manual => {
        manualJurisdictionMap.set(manual.id, manual.jurisdiction.id);
        leManualIds.push(manual.id);
      });

    console.log('Created manual ID to jurisdiction ID mapping:', manualJurisdictionMap);

    // Fetch full content for all Law Enforcement manuals only if we need to create written directives
    if (createWrittenDirectives) {
      await fetchLEManuals(leManualIds);
    }

    console.log('Creating CALEA standards, substandards, and requirements...');
    
    // Process each standard in the CALEA data
    for (const [standardKey, standardData] of Object.entries(caleaData)) {
      let createdStandard;
      
      // Check if standard already exists
      if (existingStandardsMap.has(standardKey)) {
        console.log(`Standard ${standardKey} already exists, using existing...`);
        createdStandard = existingStandardsMap.get(standardKey);
      } else {
        // Create the main standard
        const standard: AccreditationStandardNew = {
          heading: standardKey,
          title: standardData.title,
          position: parseInt(standardKey),
          draftContent: randomContent(),
          status: 'ENABLED'
        };

        console.log(`Creating standard: ${standard.title}`);
        createdStandard = await createStandard(accreditationId, versionId, standard);

        // Search for matching manual content and create written directives if enabled
        if (createWrittenDirectives) {
          const standardMatches = await searchManualContent(standard.title);
          console.log('standardMatches', standardMatches);
          for (const match of standardMatches) {
            const jurisdictionId = manualJurisdictionMap.get(match.manualId);
            // console.log('match', match, match.manualId, jurisdictionId);
            if (jurisdictionId) {
              console.log('Creating written directive for standard', match.contentUri);
              await createWrittenDirective(
                accreditationId,
                versionId,
                createdStandard.id,
                jurisdictionId,
                { contentUri: match.contentUri }
              );
            }
          }
        }
      }

      // Process substandards
      if (standardData.substandards) {
        for (const [substandardKey, substandardData] of Object.entries(standardData.substandards)) {
          let createdSubstandard;
          
          // Check if substandard already exists
          if (existingStandardsMap.has(substandardKey)) {
            console.log(`Substandard ${substandardKey} already exists, using existing...`);
            createdSubstandard = existingStandardsMap.get(substandardKey);
          } else {
            const substandard: AccreditationStandardNew = {
              heading: substandardKey,
              title: substandardData.title,
              position: parseInt(substandardKey.split('.')[1]),
              draftContent: randomContent(),
              status: 'ENABLED',
              parentStandardId: createdStandard.id
            };

            console.log(`Creating substandard: ${substandard.title}`);
            createdSubstandard = await createStandard(accreditationId, versionId, substandard);

            // Search for matching manual content and create written directives if enabled
            if (createWrittenDirectives) {
              const substandardMatches = await searchManualContent(substandard.title);
              for (const match of substandardMatches) {
                const jurisdictionId = manualJurisdictionMap.get(match.manualId);
                // console.log('match', match, match.manualId, jurisdictionId);
                if (jurisdictionId) {
                  console.log('Creating written directive for substandard', match.contentUri);
                  await createWrittenDirective(
                    accreditationId,
                    versionId,
                    createdSubstandard.id,
                    jurisdictionId,
                    { contentUri: match.contentUri }
                  );
                }
              }
            }
          }

          // Process requirements
          if (substandardData.requirements) {
            for (const [requirementKey, requirementData] of Object.entries(substandardData.requirements)) {
              // Check if requirement already exists
              if (existingStandardsMap.has(requirementKey)) {
                console.log(`Requirement ${requirementKey} already exists, skipping...`);
                continue;
              }

              const requirement: AccreditationStandardNew = {
                heading: requirementKey,
                title: requirementData.title,
                position: parseInt(requirementKey.split('.')[2]),
                draftContent: randomContent(),
                status: 'ENABLED',
                parentStandardId: createdSubstandard.id
              };

              console.log(`Creating requirement: ${requirement.title}`);
              const createdRequirement = await createStandard(accreditationId, versionId, requirement);

              // Search for matching manual content and create written directives if enabled
              if (createWrittenDirectives) {
                const requirementMatches = await searchManualContent(requirement.title);
                for (const match of requirementMatches) {
                  const jurisdictionId = manualJurisdictionMap.get(match.manualId);
                  // console.log('match', match, match.manualId, jurisdictionId);
                  if (jurisdictionId) {
                    console.log('Creating written directive for requirement', match.contentUri);
                    await createWrittenDirective(
                      accreditationId,
                      versionId,
                      createdRequirement.id,
                      jurisdictionId,
                      { contentUri: match.contentUri }
                    );
                  }
                }
              }
            }
          }
        }
      }
    }

    console.log('setCaleaStandards completed successfully!');
  } catch (error) {
    console.error('Error in setCaleaStandards:', error);
  }
}; 